import { Context, ContextProvider, ContextRoot } from '@lit/context'
import { ReactiveElement } from 'lit'


new ContextRoot().attach(document.documentElement)


type AnyContext = Context<unknown, unknown>

type ProvidedMember = {
  context: AnyContext
  read: (host: object) => unknown
}

type MemberStorage<ValueType> = {
  read: (host: object) => ValueType
  write: (host: object, value: ValueType) => void
}

const bodyProviders = new Map<AnyContext, ContextProvider<AnyContext>>()
const providedMembers = new Map<object, ProvidedMember[]>()
const connectedHosts = new Map<object, object[]>()
const followedClasses = new WeakSet<object>()

const prototypeOf = (host: object): object => Object.getPrototypeOf(host) as object

const connectedHostsOf = (prototype: object): object[] => connectedHosts.get(prototype) ?? []

/**
 * Tells whether this host is the one that fills the body contexts of its class.
 *
 * The oldest connected host owns them, so two providers of the same kind on one page cannot
 * overwrite each other.
**/
const isOwner = (host: object): boolean => connectedHostsOf(prototypeOf(host))[0] === host

/**
 * Creates the provider for a context the first time the context is filled, and updates it after.
 *
 * The provider is hosted on `document.body`, which is an ancestor of every consumer on the page.
**/
const fillProvider = (context: AnyContext, value: unknown): void => {
  const known = bodyProviders.get(context)
  if (known) {
    known.setValue(value)
    return
  }

  const provider = new ContextProvider(document.body, { context, initialValue: value })
  bodyProviders.set(context, provider)
  provider.hostConnected()
}

/**
 * Fills a body context directly, for a value that no single host member owns.
**/
export const setBodyContextValue = <ValueType>(context: Context<unknown, ValueType>, value: ValueType): void =>
  fillProvider(context as AnyContext, value)

/**
 * Finds where a provided member keeps its value.
 *
 * A member that `@property` or `@state` decorates already has an accessor on the prototype, and
 * that accessor stays in charge of the value, whichever halves it has. A member that only has a
 * getter is read when its host takes the contexts over, and nothing ever writes to it. A plain
 * field has no accessor, so it gets storage of its own, one value per host.
**/
const storageFor = <ValueType>(prototype: object, property: PropertyKey): MemberStorage<ValueType> => {
  const accessor = Object.getOwnPropertyDescriptor(prototype, property)
  if (accessor && (accessor.get || accessor.set)) {
    const { get, set } = accessor
    return {
      read: (host) => get?.call(host) as ValueType,
      write: (host, value) => set?.call(host, value),
    }
  }

  const values = new WeakMap<object, ValueType>()
  return {
    read: (host) => values.get(host) as ValueType,
    write: (host, value) => values.set(host, value),
  }
}

/**
 * Sends everything this host holds, so consumers see the values of the host that owns them.
**/
const sendStoredValues = (host: object): void => {
  const members = providedMembers.get(prototypeOf(host)) ?? []
  members.forEach(({ context, read }) => fillProvider(context, read(host)))
}

/**
 * Lines this host up behind the hosts of its class that are already connected.
 *
 * It owns the contexts right away when no other host of its class is connected. Otherwise it
 * keeps its own values, and sends them if it becomes the owner later.
**/
const addProvidingHost = (host: object): void => {
  const prototype = prototypeOf(host)
  connectedHosts.set(prototype, [...connectedHostsOf(prototype), host])

  if (isOwner(host)) sendStoredValues(host)
}

/**
 * Takes this host out of the line, and hands the contexts to the next host of its class.
 *
 * That next host is connected already, so its values replace the ones the leaving owner left
 * behind. When no host of the class is left, the contexts keep the values they have.
**/
const removeProvidingHost = (host: object): void => {
  const wasOwner = isOwner(host)
  const prototype = prototypeOf(host)
  const remaining = connectedHostsOf(prototype).filter((connected) => connected !== host)
  connectedHosts.set(prototype, remaining)

  const nextOwner = remaining[0]
  if (wasOwner && nextOwner) sendStoredValues(nextOwner)
}

/**
 * Keeps track of the connected hosts of a class as its elements connect and disconnect.
 *
 * This is what makes the decorator enough on its own, with no lifecycle code in the host. One
 * initializer per class covers every member that class provides, and every element it creates.
**/
const followHostLifecycle = (prototype: object): void => {
  const hostClass = (prototype as { constructor: typeof ReactiveElement }).constructor
  if (followedClasses.has(hostClass)) return
  followedClasses.add(hostClass)

  hostClass.addInitializer((host) => host.addController({
    hostConnected: () => addProvidingHost(host),
    hostDisconnected: () => removeProvidingHost(host),
  }))
}

/**
 * Property decorator that hosts the value on `document.body` instead of on the element.
 *
 * Consumers anywhere on the page then reach the value, without the element being their ancestor.
 * Each write goes to the body, but only from the host that owns the contexts. The decorator also
 * records the member, so a host sends the values it stored before it became the owner.
 *
 * Place it above `@property` or `@state`, because the decorator that runs last owns the accessor
 * on the prototype. Below them it loses the accessor, and the member provides nothing.
**/
export const provideOnBody = <ValueType>({ context }: { context: Context<unknown, ValueType> }) =>
  <KeyType extends PropertyKey>(prototype: { [PropKey in KeyType]: ValueType }, property: KeyType): void => {
    const storage = storageFor<ValueType>(prototype, property)

    Object.defineProperty(prototype, property, {
      configurable: true,
      enumerable: true,
      get(this: object): ValueType {
        return storage.read(this)
      },
      set(this: object, value: ValueType) {
        storage.write(this, value)
        if (isOwner(this)) fillProvider(context, storage.read(this))
      },
    })

    const members = providedMembers.get(prototype) ?? []
    members.push({ context, read: storage.read })
    providedMembers.set(prototype, members)

    followHostLifecycle(prototype)
  }
